import { useEffect, useRef, useState } from 'react'
import Editor from 'react-simple-code-editor'
import Prism from 'prismjs'
import 'prismjs/components/prism-python'
import 'prismjs/components/prism-sql'
import { AlertCircle, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { postJson } from './types'

type Copy = Record<string, string>

export interface CodeProblem { line: number; col: number; severity: 'error' | 'warning'; message: string }

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/** A code field for a trigger's Python analysis or SQL: highlighted, with line numbers, and checked by Forge while it is
 *  written (Python's own parser; SQL through the trigger guard and ClickHouse's parser). Problems point at their line. */
export function CodeField({ language, value, onChange, lintUrl, copy, minLines = 10 }: {
  language: 'python' | 'sql'; value: string; onChange: (v: string) => void; lintUrl: string; copy: Copy; minLines?: number
}) {
  const [problems, setProblems] = useState<CodeProblem[]>([])
  const [checking, setChecking] = useState(false)
  const [failed, setFailed] = useState(false)
  const run = useRef(0)

  useEffect(() => {
    const mine = ++run.current
    setProblems([]); setFailed(false)
    setChecking(!!value.trim())
    if (!value.trim()) return
    const controller = new AbortController()
    const timer = window.setTimeout(async () => {
      setChecking(true)
      try {
        const out = await postJson<{ problems: CodeProblem[] }>(lintUrl, { language, code: value }, controller.signal)
        if (mine === run.current) { setProblems(out.problems ?? []); setFailed(false) }
      } catch {
        if (mine === run.current) setFailed(true)
      } finally {
        if (mine === run.current) setChecking(false)
      }
    }, 700)
    return () => { ++run.current; window.clearTimeout(timer); controller.abort() }
  }, [value, language, lintUrl])

  const lines = Math.max(minLines, value.split('\n').length)
  const marked = new Map(problems.map((p) => [p.line, p.severity]))
  const highlight = (code: string) => {
    const grammar = Prism.languages[language]
    const html = grammar ? Prism.highlight(code, grammar, language) : escapeHtml(code)
    return html.split('\n').map((line, index) => {
      const severity = marked.get(index + 1)
      const tone = severity === 'error' ? 'code-line-error' : severity === 'warning' ? 'code-line-warning' : ''
      return `<span class="block ${tone}">${line || ' '}</span>`
    }).join('')
  }
  const errors = problems.filter((p) => p.severity === 'error').length
  const warnings = problems.length - errors

  return (
    <div className="grid gap-2">
      <div className="agent-code relative overflow-auto rounded-xl border border-border bg-muted/20 focus-within:ring-2 focus-within:ring-ring/40">
        <div className="flex min-w-max">
          <div aria-hidden className="select-none border-r border-border bg-muted/40 py-3 pl-3 pr-2 text-right font-mono text-[12px] leading-6 text-muted-foreground/70">
            {Array.from({ length: lines }, (_, i) => (
              <div key={i} className={cn(marked.get(i + 1) === 'error' && 'font-semibold text-destructive',
                marked.get(i + 1) === 'warning' && 'text-amber-600 dark:text-amber-300')}>{i + 1}</div>
            ))}
          </div>
          <Editor value={value} onValueChange={onChange} highlight={highlight} padding={12} tabSize={4} insertSpaces
            textareaClassName="focus:outline-none whitespace-pre" preClassName="whitespace-pre"
            className="min-w-[32rem] flex-1 font-mono text-[12px] leading-6 text-foreground [&_textarea]:whitespace-pre"
            style={{ minHeight: `${lines * 1.5 + 1.5}rem` }} />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {checking ? (
          <span className="inline-flex items-center gap-1 text-muted-foreground"><Loader2 className="size-3 animate-spin" aria-hidden />{copy.code_checking}</span>
        ) : failed ? (
          <span className="text-muted-foreground">{copy.code_check_unavailable}</span>
        ) : problems.length === 0 && value.trim() ? (
          <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300"><CheckCircle2 className="size-3" aria-hidden />{copy.code_ok}</span>
        ) : problems.length > 0 ? (
          <span className="text-muted-foreground">{[errors && `${errors} ${copy.code_errors}`, warnings && `${warnings} ${copy.code_warnings}`].filter(Boolean).join(' · ')}</span>
        ) : null}
      </div>
      {problems.length > 0 && (
        <ul className="grid gap-1 text-xs">
          {problems.map((p, index) => (
            <li key={index} className={cn('flex items-start gap-2 rounded-lg px-2.5 py-1.5',
              p.severity === 'error' ? 'bg-destructive/5 text-destructive' : 'bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100')}>
              {p.severity === 'error' ? <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden /> : <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />}
              <span><span className="font-mono font-medium">{copy.code_line} {p.line}:{p.col}</span> {p.message}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
